// types.ts - Type definitions for Trench Crusade Warband Manager

export interface ModelStats {
  movement: number;
  melee: number;
  ranged: number;
  baseArmour: number;
  finalArmour: number;
}

export interface Skill {
  name: string;
  table: string;
  description?: string;
}

export interface ModelUpgrade {
  id: string;
  name: string;
  cost?: number; // ducats
  limit?: number; // Maximum number of models that can have this upgrade active
  description?: string; // Optional custom description
  effects: {
    addKeywords?: string[];
    modifyStats?: { [statName: string]: number };
    addAbilities?: Array<{ name: string; description: string }>;
    addInbuiltEquipment?: InbuiltEquipment[];
    addItems?: string[]; // Array of item template IDs
  };
}

export interface InbuiltEquipment {
  id: string;
  name: string;
  category: string;
  keywords: string[];
  range?: string;
  armourModifier?: string;
  notes?: string;
}

export interface ModelTemplate {
  id: string;
  templateName: string;
  name: string;
  class: string;
  type: "elite" | "trooper";
  cost: number;
  costCurrency: "ducats" | "glory";
  stats: ModelStats;
  keywords: string[];
  abilities: string[] | { name: string; description: string }[];
  skills: Skill[];
  itemTemplateIds: string[];
  inbuiltEquipment?: InbuiltEquipment[];
  upgrades?: ModelUpgrade[];
  limit?: number; // Maximum number of models of this class that can exist in warband
}

export interface ItemTemplate {
  id: string;
  templateName: string;
  name: string;
  category: string;
  keywords: string[];
  range: string;
  armourModifier: string;
  notes: string;
  cost: number;
  costCurrency: "free" | "ducats" | "glory";
  limit?: number; // Maximum number of items of this name that can exist in warband
  restrictions?: string[]; // e.g., ["ELITE only", "Brazen Bull only"]
}

export interface PremadeItemTemplate {
  name: string;
  category: string;
  keywords: string[];
  range?: string;
  armourModifier?: string;
  notes?: string;
  cost: number;
  costCurrency: "ducats" | "glory";
  limit?: number;
  limitScope?: 'name' | 'templateName';
  restrictions?: string[];
}

export interface PremadeModelTemplate {
  name: string;
  class: string;
  type: "elite" | "trooper";
  cost: number;
  costCurrency: "ducats" | "glory";
  stats: ModelStats;
  keywords: string[];
  abilities: string[] | { name: string; description: string }[];
  skills: Skill[];
  itemTemplateIds: string[];
  inbuiltEquipment?: InbuiltEquipment[];
  upgrades?: ModelUpgrade[];
  limit?: number;
  limitScope?: 'class' | 'template';
}

export interface Warband {
  ducats: number;
  glory: number;
  battles: number;
  explorationSkills: string[];
  name: string;
  factionLabel?: string; // Display-only: selected faction/variant/homebrew label for this profile
  templateSetName?: string;
  warchest: string[]; // Array of item IDs stored in the warchest
}

export interface Item {
  id: string;
  name: string;
  category: string;
  keywords: string[];
  range?: string;
  armourModifier?: string;
  notes?: string;
  cost?: number;
  costCurrency?: "free" | "ducats" | "glory";
  isInbuilt?: boolean;
}

export interface Model {
  id: string;
  name: string;
  class: string;
  type?: "elite" | "trooper";
  xp: number;
  cost?: number;
  costCurrency?: "ducats" | "glory";
  stats: ModelStats;
  keywords: string[];
  abilities: string[] | { name: string; description: string }[];
  skills: Skill[];
  items: string[];
  alive: boolean;
  scars: string[];
  diedOnBattle?: number;
  templateId?: string; // ID of the template this model was created from
  enabledUpgrades?: string[]; // array of upgrade IDs that are active
  upgrades?: ModelUpgrade[]; // model-specific upgrades
  reserve?: boolean; // If true, unit is in reserve and doesn't count toward warband total value
  customBgUrl?: string | null; // Optional: user-provided background image URL for sidebar/modal
}

export interface LocationReward {
  type?: 'ducats' | 'glory' | 'item' | 'skill' | 'keyword' | 'model' | 'special';
  claimed?: boolean;
  choice?: string;
  items?: string[];
  itemTemplateIds?: string[];
  addToWarchest?: boolean;
  ducats?: number;
  glory?: number;
  skills?: string[];
  addKeywords?: string[];
  targetModelIds?: string[];
  newModelTemplateId?: string;
  message?: string;
}

export interface ExplorationLocation {
  id: string;
  name: string;
  effect: string;
  table: "common" | "rare" | "legendary";
  roll: number;
  discoveredOnBattle: number;
  reward?: LocationReward;
}

export interface ExplorationResult {
  type: string;
  table: "common" | "rare" | "legendary";
  roll: number;
  location?: {
    name: string;
    effect: string;
  };
  ducatsGained: number;
}

export interface Ability {
  name: string;
  description?: string;
}

// Battle Scars/Injuries for elite models
export const BATTLE_SCARS = [
  "Dead",
  "Captured",
  "Severe Nerve Damage",
  "Hand Wound",
  "Lost an Eye",
  "Chest Wound",
  "Insomniac",
  "Head Wound",
  "Shell-shocked",
  "Dark Memory",
  "Paranoid",
  "Lost Arm",
  "Leg Wound",
  "Expensive Treatment",
  "Possessed",
  "Muscle Damage",
  "Minor Wound",
  "Robbed"
] as const;

export type BattleScar = typeof BATTLE_SCARS[number];

// Exploration Skills
export const EXPLORATION_SKILLS = [
  "Extra dice",
  "Duplicate",
  "Re-roll",
  "Set Dice",
  "Seek",
  "Circle Back",
  "Lucky"
] as const;

export type ExplorationSkill = typeof EXPLORATION_SKILLS[number];
